import { z } from 'zod'

import type { CustomImapSettingsDto } from '../types/stockImports.types'

const requiredString = (message: string) => z.string().trim().min(1, message)

const oneEmailMessage = 'Укажите один email'

const requiredSingleEmail = (message: string) =>
  z.preprocess(
    (value) => (typeof value === 'string' ? value.trim() : value),
    z
      .string({
        required_error: message,
        invalid_type_error: message,
      })
      .min(1, message)
      .email('Некорректный email')
      .refine((value) => !/[;,\s]/.test(value), oneEmailMessage)
  )

const optionalSingleEmail = z.preprocess(
  (value) => (typeof value === 'string' ? value.trim() || undefined : value),
  z
    .string()
    .email('Некорректный email')
    .refine((value) => !/[;,\s]/.test(value), oneEmailMessage)
    .optional()
)

// Свободный ввод: только "+" и цифры, до 20 символов (см. SupplierModal).
const phoneSchema = z.preprocess(
  (value) => (typeof value === 'string' ? value.trim() : value),
  z
    .string({
      required_error: 'Укажите телефон',
      invalid_type_error: 'Укажите телефон',
    })
    .min(1, 'Укажите телефон')
    .max(20, 'Не более 20 символов')
    .regex(/^\+?\d+$/, 'Только цифры и "+"')
)

const vatNumberSchema = z.preprocess(
  (value) => (typeof value === 'string' ? value.trim() : value),
  z
    .string({
      required_error: 'Укажите УНП',
      invalid_type_error: 'Укажите УНП',
    })
    .min(1, 'Укажите УНП')
    .max(14, 'УНП не должен превышать 14 символов')
    .regex(/^\d{9}$/, 'УНП должен состоять из 9 цифр')
)

const requiredTrimmedString = (message: string) =>
  z.preprocess(
    (value) => (typeof value === 'string' ? value.trim() : value),
    z
      .string({
        required_error: message,
        invalid_type_error: message,
      })
      .min(1, message)
  )

const optionalTrimmedString = z.preprocess(
  (value) => (typeof value === 'string' ? value.trim() || undefined : value),
  z.string().optional()
)

const optionalTrimmedUrlString = z.preprocess(
  (value) => (typeof value === 'string' ? value.trim() || undefined : value),
  z
    .string()
    .max(
      2000,
      'Ссылка слишком длинная. Убедитесь, что скопирована ссылка на файл, а не весь адрес страницы предпросмотра'
    )
    .optional()
)

const optionalTrimmedStringArray = z.preprocess((value) => {
  if (!Array.isArray(value)) return undefined

  const nextValue = value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean)

  return nextValue.length > 0 ? nextValue : undefined
}, z.array(z.string()).optional())

const requiredNonNegativeNumber = (message: string) =>
  z.preprocess(
    (value) => (value === null ? undefined : value),
    z
      .number({
        required_error: message,
        invalid_type_error: message,
      })
      .min(0, message)
  )

const formListRow = <T extends z.ZodRawShape>(
  shape: T
): z.ZodEffects<z.ZodObject<T>, z.infer<z.ZodObject<T>>, unknown> =>
  z.preprocess((value) => value ?? {}, z.object(shape))

const optionalNonNegativeInteger = z.preprocess(
  (value) => (value === null ? undefined : value),
  z.number().int().min(0).optional()
)

const optionalPositiveInteger = z.preprocess(
  (value) => (value === null ? undefined : value),
  z.number().int().min(1).optional()
)

const columnNumberFields = [
  'articleColumnNumber',
  'articleInchColumnNumber',
  'barcodeColumnNumber',
  'descriptionColumnNumber',
  'brandColumnNumber',
  'quantityColumnNumber',
] as const

type ColumnNumberField = (typeof columnNumberFields)[number]
type ColumnNumberValues = Partial<Record<ColumnNumberField, number | undefined>>

const duplicateColumnNumberMessage = 'Такая колонка уже выбрана'

const getDuplicateColumnFields = (values: ColumnNumberValues) => {
  const entries = columnNumberFields
    .map((field) => ({ field, value: values[field] }))
    .filter(
      (item): item is { field: ColumnNumberField; value: number } =>
        item.value !== undefined && item.value !== null
    )

  return new Set(
    entries
      .filter((item, index, array) =>
        array.some(
          (current, currentIndex) =>
            currentIndex !== index && current.value === item.value
        )
      )
      .map((item) => item.field)
  )
}

const requiredPositiveInteger = (message: string) =>
  z
    .number({
      required_error: message,
      invalid_type_error: message,
    })
    .int(message)
    .min(1, message)

const MIN_START_ROW_WITH_QUANTITY_HEADER = 2

const optionalPercentage = z.preprocess(
  (value) => (value === null ? undefined : value),
  z.number().min(1).max(100).optional()
)

export const supplierFormSchema = z.object({
  description: requiredString('Укажите наименование'),
  vatNumber: vatNumberSchema,
  fullLegalName: requiredString('Укажите полное наименование'),
  shortLegalName: requiredString('Укажите краткое наименование'),
  legalAddress: requiredString('Укажите юридический адрес'),
  postalAddress: requiredString('Укажите почтовый адрес'),
  phoneNumber: phoneSchema,
  email: requiredSingleEmail('Укажите email'),
})

export const positionCheckSchema = z.object({
  article: requiredString('Укажите артикул'),
})

export type PositionCheckValues = z.infer<typeof positionCheckSchema>

const configSourceRulesShape = z.object({
  sourceType: z.string(),
  url: optionalTrimmedUrlString,
  expectedSender: optionalSingleEmail,
  subjectMask: optionalTrimmedString,
  fileMask: optionalTrimmedString,
  imapLogin: optionalTrimmedString,
  imapPasswordEncrypted: optionalTrimmedString,
})

const addSourceIssues = (
  values: z.output<typeof configSourceRulesShape>,
  ctx: z.RefinementCtx
) => {
  if (values.sourceType === 'URL' && !values.url) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['url'],
      message: 'Укажите ссылку',
    })
  }

  if (values.sourceType === 'CUSTOM_IMAP') {
    if (!values.imapLogin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['imapLogin'],
        message: 'Укажите логин',
      })
    }

    if (!values.imapPasswordEncrypted) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['imapPasswordEncrypted'],
        message: 'Укажите пароль',
      })
    }
  }

  if (values.sourceType === 'EMAIL') {
    if (!values.expectedSender) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['expectedSender'],
        message: 'Укажите допустимого отправителя',
      })
    }

    if (!values.subjectMask) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['subjectMask'],
        message: 'Укажите маркер темы письма',
      })
    }
  }

  if (
    (values.sourceType === 'EMAIL' || values.sourceType === 'CUSTOM_IMAP') &&
    !values.fileMask
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['fileMask'],
      message: 'Укажите маску файла',
    })
  }
}

export const configSourceRulesSchema =
  configSourceRulesShape.superRefine(addSourceIssues)

export const configFormSchema = z
  .object({
    supplierId: requiredString('Выберите поставщика'),
    storeId: requiredString('Выберите склад'),
    sourceType: z.enum(['EMAIL', 'URL', 'MANUAL', 'CUSTOM_IMAP'], {
      errorMap: () => ({ message: 'Выберите источник' }),
    }),
    fileFormat: z.enum(['EXCEL', 'CSV', 'XML'], {
      errorMap: () => ({ message: 'Выберите формат файла' }),
    }),
    cronExpression: optionalTrimmedString,
    expirationHours: requiredPositiveInteger(
      'Укажите, через сколько часов считать остатки устаревшими'
    ),
    excludeWeekends: z.boolean().default(true),
    startRowIndex: optionalNonNegativeInteger,
    endRowIndex: optionalNonNegativeInteger,
    sheetSelectionType: z.enum(['ALL', 'BY_NAME', 'BY_INDEX']),
    sheetNames: optionalTrimmedString,
    sheetIndexes: optionalTrimmedString,
    url: optionalTrimmedUrlString,
    expectedSender: optionalSingleEmail,
    subjectMask: optionalTrimmedString,
    fileMask: optionalTrimmedString,
    imapHost: optionalTrimmedString,
    imapPort: optionalTrimmedString,
    imapLogin: optionalTrimmedString,
    imapPasswordEncrypted: optionalTrimmedString,
    useSsl: z.boolean().default(true),
    articleColumnNumber: requiredPositiveInteger('Укажите колонку артикула'),
    articleInchColumnNumber: optionalPositiveInteger,
    barcodeColumnNumber: optionalPositiveInteger,
    descriptionColumnNumber: requiredPositiveInteger(
      'Укажите колонку наименования'
    ),
    brandColumnNumber: optionalPositiveInteger,
    quantityColumnNumber: requiredPositiveInteger('Укажите колонку остатка'),
    allowedBrands: optionalTrimmedStringArray,
    ignoredBrands: optionalTrimmedStringArray,
    fallbackBrandId: optionalTrimmedString,
    detectBrandInDescription: z.boolean().optional(),
    maxAllowedMissingPercentage: optionalPercentage,
    missingPercentageSpikeAlert: optionalPercentage,
    minLoadedPercentOfMedian: optionalPercentage,
    maxInvalidQuantityPercentage: optionalPercentage,
    stalledAlertBusinessDays: optionalPositiveInteger,
    expectedQuantityHeader: optionalTrimmedString,
    textQuantityMapping: z
      .array(
        formListRow({
          key: requiredTrimmedString('Укажите текстовое значение'),
          value: requiredNonNegativeNumber('Укажите число'),
        })
      )
      .optional(),
  })
  .superRefine((values, ctx) => {
    addSourceIssues(values, ctx)

    if (values.sourceType !== 'EMAIL' && !values.cronExpression) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cronExpression'],
        message: 'Выберите интервал проверки',
      })
    }

    if (values.startRowIndex !== undefined && values.startRowIndex < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startRowIndex'],
        message: 'Укажите строку от 1',
      })
    }

    if (
      values.expectedQuantityHeader &&
      (values.startRowIndex === undefined ||
        values.startRowIndex < MIN_START_ROW_WITH_QUANTITY_HEADER)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startRowIndex'],
        message:
          'Укажите начальную строку данных не меньше 2: заголовок берётся из строки над ней',
      })
    }

    if (values.endRowIndex !== undefined && values.endRowIndex < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endRowIndex'],
        message: 'Укажите строку от 1',
      })
    }

    if (
      values.startRowIndex !== undefined &&
      values.endRowIndex !== undefined &&
      values.endRowIndex < values.startRowIndex
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endRowIndex'],
        message: 'Конечная строка должна быть не меньше начальной',
      })
    }

    if (
      values.fileFormat === 'EXCEL' &&
      values.sheetSelectionType === 'BY_NAME' &&
      !values.sheetNames
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sheetNames'],
        message: 'Укажите имена листов',
      })
    }

    if (
      values.fileFormat === 'EXCEL' &&
      values.sheetSelectionType === 'BY_INDEX' &&
      !values.sheetIndexes
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sheetIndexes'],
        message: 'Укажите номера листов',
      })
    }

    if (
      values.fileFormat === 'EXCEL' &&
      values.sheetSelectionType === 'BY_INDEX' &&
      values.sheetIndexes &&
      !values.sheetIndexes.split(',').every((item) => /^\d+$/.test(item.trim()))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sheetIndexes'],
        message: 'Номера листов — целые числа от 0 через запятую',
      })
    }

    if (
      values.brandColumnNumber === undefined &&
      !values.fallbackBrandId &&
      !values.detectBrandInDescription
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['brandColumnNumber'],
        message: 'Укажите бренд',
      })
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fallbackBrandId'],
        message: 'Укажите бренд',
      })
    }

    getDuplicateColumnFields(values).forEach((field) => {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [field],
        message: duplicateColumnNumberMessage,
      })
    })
  })

export const MASKED_IMAP_PASSWORD = '********'
export const IMAP_PASSWORD_REENTER_MESSAGE =
  'Сервер или логин почты изменились — введите пароль заново'

const normalizeMailboxPart = (value?: string | null) =>
  value?.trim().toLowerCase() ?? ''

export const needsImapPasswordReentry = (
  values: {
    imapHost?: string
    imapPort?: string
    imapLogin?: string
    imapPasswordEncrypted?: string
  },
  storedMailbox?: CustomImapSettingsDto | null
) => {
  if (values.imapPasswordEncrypted?.trim() !== MASKED_IMAP_PASSWORD)
    return false
  if (!storedMailbox) return true

  return (
    normalizeMailboxPart(values.imapHost) !==
      normalizeMailboxPart(storedMailbox.imapHost) ||
    normalizeMailboxPart(values.imapLogin) !==
      normalizeMailboxPart(storedMailbox.imapLogin)
  )
}
